/**
 * MailReadingPane — right reading pane for the Eingang split-view (T5).
 * Ported from v0 mail-reading-pane.tsx into OUR stack:
 * - OUR Tabs (useState-based)
 * - OUR DecisionBar (primary/alternatives/reject)
 * - OUR Badge, Button, StatusDot
 * - Hand-rolled inputs/textareas, overflow menu, no sonner
 * [Source: v0 components/brokly/mail-reading-pane.tsx]
 */
import { useEffect, useState } from 'react';
import {
  ChevronUp,
  ChevronDown,
  PanelRightOpen,
  PanelRightClose,
  CircleCheck,
  CircleDot,
  Circle,
  Sparkles,
  Paperclip,
  Send,
  ClipboardList,
  AlertTriangle,
  Search,
  User,
  Check,
  X,
  MoreHorizontal,
  Archive,
  Download,
  Lock,
  EyeOff,
  Eye,
} from 'lucide-react';
import { cn } from '../../../lib/cn';
import { apiClient } from '../../../lib/api-client';
import { mailTeamMarkerName } from '../mail-visibility';
import { MailTeamBadge } from './MailTeamBadge';
import { saveBlob } from '../../../lib/save-blob';
import { sanitizeEmailHtml } from '../../../lib/sanitize-email-html';
import { Button } from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import { DecisionBar } from '../../../components/data/DecisionBar';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../../components/ui/Tabs';
import { KonfidenzBadge, KonfidenzBar } from './konfidenz';
import { EntityLink } from './EntityLink';
import { AssistenzBlock } from './AssistenzBlock';
import { ComposeDialog } from './ComposeDialog';
import type { ComposeDraft, SendingMailbox } from './ComposeDialog';
import { resolveAssistKonfidenz } from '../api/comm-assist-api';
import type { CommAssist } from '../api/comm-assist-api';
import type { EingangMail, KundeOption, MailAction, Triage, VisibilityTier } from '../types';
import { KANAL_META, TRIAGE_META, KUNDEN_OPTIONS } from '../mock';

/**
 * Extract the bare addr-spec from a "Name <email>" (RFC 5322 name-addr) string so the
 * reply "An" field is a plain email the compose <input type="email"> accepts. A bare
 * address (no angle brackets) passes through unchanged.
 */
export function parseAddrSpec(s: string): string {
  return (s.match(/<([^>]+)>/)?.[1] ?? s).trim();
}

import { ReducedTierOverlay } from './ReducedTierOverlay';

/** Minimal inline feedback banner (replaces sonner toast) */
function FeedbackBanner({
  message,
  onDismiss,
}: {
  message: string;
  onDismiss: () => void;
}) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 2500);
    return () => clearTimeout(t);
  }, [message, onDismiss]);

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-4 right-4 z-50 flex items-center gap-2 rounded-lg border border-sem-ok/40 bg-sem-ok-bg px-4 py-2.5 text-sm text-sem-ok shadow-md"
    >
      <Check className="size-4 shrink-0" />
      {message}
    </div>
  );
}

/**
 * Hand-rolled overflow menu ("⋯") — the real Archivieren action (UI-U9: the
 * Weiterleiten/Bearbeiten no-ops were removed; reply/edit go via the modal) plus the
 * Berechtigungskonzept V3.0 §2.2 Privat-Markierung toggle, shown only to authorized
 * parties (`privateAction` is null/undefined when the caller is not the sender/an
 * internal to/cc recipient — the action is simply absent, not disabled).
 */
function OverflowMenu({
  onAction,
  privateAction,
  threadPrivateAction,
}: {
  onAction: (a: MailAction) => void;
  /** null/undefined ⇒ caller not authorized (sender/internal to/cc only) → item absent. */
  privateAction?: { label: string; icon: typeof EyeOff; onClick: () => void } | null;
  /** P4: mark/unmark the WHOLE thread private. null/undefined ⇒ item absent (same gate). */
  threadPrivateAction?: { label: string; icon: typeof EyeOff; onClick: () => void } | null;
}) {
  const [open, setOpen] = useState(false);
  const items: { label: string; action: MailAction; icon: typeof Archive }[] = [
    { label: 'Archivieren', action: 'archivieren', icon: Archive },
  ];
  return (
    <div className="relative">
      <Button
        variant="ghost"
        size="icon"
        aria-label="Weitere Aktionen"
        onClick={() => setOpen((v) => !v)}
      >
        <MoreHorizontal />
      </Button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 z-20 mt-1 w-52 overflow-hidden rounded-md border border-border bg-popover shadow-md">
            {items.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.action}
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    onAction(item.action);
                  }}
                  className={cn(
                    'flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-muted focus-visible:bg-muted focus-visible:outline-none',
                    item.action === 'ablehnen' ? 'text-muted-foreground' : 'text-foreground',
                  )}
                >
                  <Icon className="size-4 shrink-0 text-muted-foreground" />
                  {item.label}
                </button>
              );
            })}
            {privateAction && (
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  privateAction.onClick();
                }}
                className="flex w-full items-center gap-2 border-t border-border/60 px-3 py-2 text-left text-sm text-foreground transition-colors hover:bg-muted focus-visible:bg-muted focus-visible:outline-none"
              >
                <privateAction.icon className="size-4 shrink-0 text-muted-foreground" />
                {privateAction.label}
              </button>
            )}
            {threadPrivateAction && (
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  threadPrivateAction.onClick();
                }}
                className="flex w-full items-center gap-2 border-t border-border/60 px-3 py-2 text-left text-sm text-foreground transition-colors hover:bg-muted focus-visible:bg-muted focus-visible:outline-none"
              >
                <threadPrivateAction.icon className="size-4 shrink-0 text-muted-foreground" />
                {threadPrivateAction.label}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

interface MailReadingPaneProps {
  mail: EingangMail | null;
  index: number;
  total: number;
  onPrev: () => void;
  onNext: () => void;
  onDecision: (action: MailAction) => void;
  onCreateTask: (mail: EingangMail) => void;
  onOpenKunde: (kundeId: string) => void;
  onMatchKunde: (kunde: KundeOption) => void;
  hasLinkedTask: boolean;
  feedback?: string | null;
  /** KI-Assistenz (feature-detected; UI-U9). null → the AssistenzBlock is absent. */
  assist?: CommAssist | null;
  /** MC6: the connected mailbox a reply WOULD send from → "Von: <email>". */
  sendingMailbox?: SendingMailbox | null;
  /** MC6: no connected mailbox → open the provider-choice modal from the compose hint. */
  onConnectRequest?: () => void;
  /** PART B: the caller already has an open Freigabe request on this comm → pending badge. */
  accessRequestPending?: boolean;
  /** PART B: created_at of that open request (shown in the pending badge). */
  accessRequestedAt?: string | null;
  /** W1: "Freigabe anfragen" on a reduced-tier mail — carries the mandatory Begründung. */
  onRequestAccess?: (reason: string) => void;
  /** W1: the request POST is in flight (disables the button). */
  requestInFlight?: boolean;
  /** Reply flow: the communication id this mail is on (KI-Vorschlag generieren + threading). */
  commId?: string | null;
  /** Reply flow: real send handler → the reply composer shows a "Senden" button. */
  onReplySend?: (draft: ComposeDraft) => Promise<void>;
  /**
   * Berechtigungskonzept V3.0 §2.2 (Bereich 1 Privat-Markierung): mark/unmark the
   * selected mail private. Only called when the OverflowMenu's item is shown, which
   * itself only renders when `mail.canMarkPrivate` is true (sender/internal to/cc).
   */
  onTogglePrivate?: () => void;
  /** The mark-private / unmark-private POST is in flight (disables re-clicking). */
  privateActionInFlight?: boolean;
  /**
   * Berechtigungskonzept V3.0 P4 — mark/unmark the WHOLE e-mail thread private. Shown next
   * to the single-mail toggle under the same authorization gate (mail.canMarkPrivate).
   */
  onToggleThreadPrivate?: () => void;
  /** The mark/unmark-thread-private POST is in flight (disables re-clicking). */
  threadPrivateActionInFlight?: boolean;
}

export function MailReadingPane({
  mail,
  index,
  total,
  onPrev,
  onNext,
  onDecision,
  onCreateTask,
  onOpenKunde,
  onMatchKunde,
  hasLinkedTask,
  feedback,
  assist,
  sendingMailbox = null,
  onConnectRequest,
  accessRequestPending = false,
  accessRequestedAt = null,
  onRequestAccess,
  requestInFlight = false,
  commId = null,
  onReplySend,
  onTogglePrivate,
  privateActionInFlight = false,
  onToggleThreadPrivate,
  threadPrivateActionInFlight = false,
}: MailReadingPaneProps) {
  const [panelOpen, setPanelOpen] = useState(false);
  const [tab, setTab] = useState('inhalt');
  const [matchOpen, setMatchOpen] = useState(false);
  const [matchQuery, setMatchQuery] = useState('');
  const [composeOpen, setComposeOpen] = useState(false);
  // Per-mail local compose drafts (survive message navigation — UI-U9 AC4).
  const [drafts, setDrafts] = useState<Record<string, ComposeDraft>>({});
  // Same rule for the Freigabe-Begründung: it is the justification for THIS mail, and it
  // is audited, so carrying it to the next mail would file a reason written about another.
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [taskOpen, setTaskOpen] = useState(false);
  const [taskTitel, setTaskTitel] = useState('');
  const [taskBeschreibung, setTaskBeschreibung] = useState('');
  const [anhangFehler, setAnhangFehler] = useState<string | null>(null);

  useEffect(() => {
    setComposeOpen(false);
    setTaskOpen(false);
    setMatchOpen(false);
    setMatchQuery('');
    setAnhangFehler(null);
  }, [mail?.id]);

  useEffect(() => {
    if (!mail || !taskOpen) return;
    setTaskTitel(`Nachverfolgen: ${mail.betreff}`);
    setTaskBeschreibung(
      `Quelle: ${KANAL_META[mail.kanal].label} vom ${mail.datum}, Von: ${mail.absender}, Klassifikation: ${mail.kategorie}`,
    );
  }, [taskOpen, mail]);

  if (!mail) {
    return (
      <div className="flex h-full items-center justify-center p-8 text-center text-sm text-muted-foreground">
        Wähle links eine Nachricht, um sie hier zu lesen.
      </div>
    );
  }

  const triage = TRIAGE_META[mail.triage as Triage];
  const kanal = KANAL_META[mail.kanal];
  const KanalIcon = kanal.icon;
  // PART B: reduced tiers hide the body (the API already stripped it) → overlay.
  const tier: VisibilityTier = mail.tier ?? 'full';
  const reduced = tier === 'title' || tier === 'metadata';
  const restrictedTeam = mailTeamMarkerName(mail.restrictedToTeam);

  const matchResults = KUNDEN_OPTIONS.filter((k) =>
    k.name.toLowerCase().includes(matchQuery.trim().toLowerCase()),
  );

  function handleAction(action: MailAction) {
    if (action === 'antworten') {
      setComposeOpen(true);
      return;
    }
    if (action === 'aufgabe') {
      setTaskOpen(true);
      return;
    }
    onDecision(action);
  }

  function copyDraft() {
    if (assist?.draft) void navigator.clipboard?.writeText(assist.draft);
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Feedback banner */}
      {feedback && <FeedbackBanner message={feedback} onDismiss={() => {}} />}

      {/* Kopfbereich */}
      <div className="flex shrink-0 flex-col gap-3 border-b p-4">
        {/* Zeile 1: ID / Kanal / Datum ← → Navigation */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
            <span className="mono-num font-medium uppercase text-foreground/70">{mail.id}</span>
            <span>·</span>
            <Badge variant="neutral" className="gap-1">
              <KanalIcon className="size-3" />
              {kanal.label}
            </Badge>
            <span>·</span>
            <span className="tabular-nums">{mail.datum}</span>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <span className="mr-1 text-xs tabular-nums text-muted-foreground">
              {index + 1} von {total}
            </span>
            <Button
              variant="outline"
              size="icon"
              onClick={onPrev}
              aria-label="Vorherige Nachricht (K)"
              className="size-7"
            >
              <ChevronUp className="size-3.5" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={onNext}
              aria-label="Nächste Nachricht (J)"
              className="size-7"
            >
              <ChevronDown className="size-3.5" />
            </Button>
            <div className="mx-1 h-5 w-px bg-border" />
            <Button
              variant={panelOpen ? 'secondary' : 'outline'}
              size="icon"
              onClick={() => setPanelOpen((v) => !v)}
              aria-label="Tags & Notizen ein-/ausblenden"
              aria-pressed={panelOpen}
              className="size-7"
            >
              {panelOpen ? (
                <PanelRightClose className="size-3.5" />
              ) : (
                <PanelRightOpen className="size-3.5" />
              )}
            </Button>
          </div>
        </div>

        {/* Zeile 2: Betreff + Status + Konfidenz */}
        <div className="flex flex-wrap items-center gap-2">
          {tier === 'metadata' ? (
            <h2 className="flex items-center gap-1.5 text-balance text-base font-semibold italic text-muted-foreground">
              <Lock className="size-4" />
              Betreff nicht freigegeben
            </h2>
          ) : (
            <h2 className="text-balance text-base font-semibold text-foreground">
              {mail.betreff || '(kein Betreff)'}
            </h2>
          )}
          <Badge variant={triage.tone}>{triage.label}</Badge>
          {mail.isPrivate && (
            <Badge variant="neutral" className="gap-1">
              <EyeOff className="size-3" />
              Privat
            </Badge>
          )}
          {restrictedTeam != null && <MailTeamBadge team={restrictedTeam} size="md" />}
          {!reduced && mail.hasTask !== false && <KonfidenzBadge value={mail.konfidenz} />}
        </div>

        {/* Zeile 3: Absender + Klassifikation */}
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
          <span className="font-medium text-foreground">{mail.absender}</span>
          <span className="text-muted-foreground">·</span>
          <span className="text-muted-foreground">{mail.absenderRolle}</span>
          <Badge variant="neutral" className="ml-0.5">
            {mail.kategorie}
          </Badge>
        </div>

        {/* Zeile 4: Kunden-Match.
            - A real client link (kundeId present) → show "Kunde: <name>" (e.g. a firm
              auto-created from the sender's domain by genesis).
            - No link but flagged for triage (!kundeMatched) → "Kein Match — zuordnen".
            - No link and not flagged (e.g. no-task internal mail) → show nothing (no nag). */}
        {mail.kundeId ? (
          <div className="flex items-center gap-1.5 text-sm">
            <CircleCheck className="size-4 shrink-0 text-sem-ok" />
            <span className="text-muted-foreground">Kunde:</span>
            <EntityLink onActivate={() => onOpenKunde(mail.kundeId)}>
              {mail.kunde || mail.kundeId}
            </EntityLink>
          </div>
        ) : mail.kundeMatched ? null : matchOpen ? (
          <div className="flex flex-col gap-1.5 rounded-md border border-sem-decide/30 bg-sem-decide-bg/50 p-2">
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
                <input
                  autoFocus
                  type="text"
                  value={matchQuery}
                  onChange={(e) => setMatchQuery(e.target.value)}
                  placeholder="Kunde suchen …"
                  aria-label="Kunde für Zuordnung suchen"
                  className="h-8 w-full rounded-md border border-border bg-background pl-8 pr-2.5 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setMatchOpen(false);
                  setMatchQuery('');
                }}
              >
                Abbrechen
              </Button>
            </div>
            <ul className="max-h-40 overflow-y-auto rounded-md border bg-popover">
              {matchResults.length > 0 ? (
                matchResults.map((k) => (
                  <li key={k.id}>
                    <button
                      type="button"
                      onClick={() => {
                        onMatchKunde(k);
                        setMatchOpen(false);
                        setMatchQuery('');
                      }}
                      className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-sm text-foreground transition-colors hover:bg-muted focus-visible:bg-muted focus-visible:outline-none"
                    >
                      <User className="size-3.5 shrink-0 text-muted-foreground" />
                      {k.name}
                    </button>
                  </li>
                ))
              ) : (
                <li className="px-2.5 py-2 text-sm text-muted-foreground">
                  Kein Kunde gefunden.
                </li>
              )}
            </ul>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setMatchOpen(true)}
            className="flex w-fit items-center gap-1.5 rounded-md border border-sem-decide/30 bg-sem-decide-bg px-2 py-1 text-sm text-sem-decide transition-colors hover:bg-sem-decide-bg/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <AlertTriangle className="size-4 shrink-0" />
            Kein Match: zuordnen
          </button>
        )}

        {/* Aufgabe erstellt-Hinweis */}
        {hasLinkedTask && (
          <div className="flex w-fit items-center gap-1.5 text-sm text-sem-ok">
            <ClipboardList className="size-4 shrink-0" />
            <span className="font-medium">Aufgabe erstellt</span>
            <span className="text-muted-foreground">· verknüpft</span>
          </div>
        )}
      </div>

      {/* DecisionBar (K1) */}
      <div className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-2 border-b bg-card p-3">
        <DecisionBar
          primary={{
            label: 'Bestätigen',
            icon: <Check className="size-4" />,
            onClick: () => handleAction('bestaetigen'),
          }}
          alternatives={[
            {
              label: 'Antworten',
              icon: <Send className="size-4" />,
              onClick: () => handleAction('antworten'),
            },
            {
              // "Folgeaufgabe": creates an ADDITIONAL follow-up to-do. Deliberately
              // NOT "Aufgabe erstellen" — that collided with the triage Pendenz the
              // intake worker already auto-creates for every inbound.
              label: 'Folgeaufgabe',
              icon: <ClipboardList className="size-4" />,
              onClick: () => handleAction('aufgabe'),
            },
          ]}
          reject={{
            label: 'Ablehnen',
            icon: <X className="size-4" />,
            onClick: () => handleAction('ablehnen'),
          }}
        />
        <div className="ml-auto">
          <OverflowMenu
            onAction={handleAction}
            privateAction={
              mail.canMarkPrivate && onTogglePrivate && !privateActionInFlight
                ? {
                    label: mail.isPrivate ? 'Nicht mehr privat' : 'Privat markieren',
                    icon: mail.isPrivate ? Eye : EyeOff,
                    onClick: onTogglePrivate,
                  }
                : null
            }
            threadPrivateAction={
              mail.canMarkPrivate && onToggleThreadPrivate && !threadPrivateActionInFlight
                ? {
                    // The visible mail's is_private is used as the thread-state proxy: marking
                    // a thread private makes this mail private too, so the label tracks it.
                    label: mail.isPrivate ? 'Thread nicht mehr privat' : 'Ganzen Thread privat markieren',
                    icon: mail.isPrivate ? Eye : EyeOff,
                    onClick: onToggleThreadPrivate,
                  }
                : null
            }
          />
        </div>
      </div>

      {/* Inhalt + optionales Tags/Notizen-Panel */}
      <div className="flex min-h-0 flex-1">
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
          <Tabs
            value={tab}
            onValueChange={setTab}
            className="flex min-h-0 flex-1 flex-col gap-0"
          >
            <TabsList className="mx-4 mt-3 shrink-0 flex-wrap justify-start gap-0.5">
              <TabsTrigger value="inhalt">Inhalt</TabsTrigger>
              <TabsTrigger value="felder">
                Felder
                <span className="ml-1 text-xs tabular-nums text-muted-foreground">
                  {mail.felder.length}
                </span>
              </TabsTrigger>
              <TabsTrigger value="pipeline">Pipeline</TabsTrigger>
              <TabsTrigger value="anhaenge">
                Anhänge
                <span className="ml-1 text-xs tabular-nums text-muted-foreground">
                  {mail.anhaenge.length}
                </span>
              </TabsTrigger>
              <TabsTrigger value="verlauf">
                Verlauf
                <span className="ml-1 text-xs tabular-nums text-muted-foreground">
                  {mail.verlauf.length}
                </span>
              </TabsTrigger>
            </TabsList>

            <div className="min-h-0 flex-1 overflow-y-auto p-4">
              <TabsContent value="inhalt" className="flex flex-col gap-6">
                {reduced ? (
                  <ReducedTierOverlay
                    tier={tier}
                    pending={accessRequestPending}
                    requestedAt={accessRequestedAt}
                    inFlight={requestInFlight}
                    onRequest={onRequestAccess}
                    reason={reasons[mail.id] ?? ''}
                    onReasonChange={(next) =>
                      setReasons((prev) => ({ ...prev, [mail.id]: next }))
                    }
                  />
                ) : (
                <section className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                      <KanalIcon className="size-4" />
                    </span>
                    <div className="flex flex-col leading-tight">
                      <span className="text-sm font-medium text-foreground">{mail.absender}</span>
                      <span className="text-xs text-muted-foreground">{mail.absenderRolle}</span>
                    </div>
                  </div>
                  {mail.html ? (
                    // HTML-only mail: render the actual body, sanitized. Scripts,
                    // event handlers and remote-object embeds are stripped; links open
                    // in a new tab with rel="noopener noreferrer" (see sanitizeEmailHtml).
                    <div
                      data-testid="mail-body-html"
                      className="overflow-x-auto rounded-md border bg-muted/40 p-3 text-sm leading-relaxed text-foreground/90 [&_a]:text-primary [&_a]:underline"
                      dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(mail.html) }}
                    />
                  ) : (
                    <p className="rounded-md border bg-muted/40 p-3 text-sm leading-relaxed text-foreground/90">
                      {mail.text}
                    </p>
                  )}
                </section>
                )}

                {!reduced && mail.hasTask !== false && <KonfidenzBar value={mail.konfidenz} />}

                {/* KI-Assistenz (feature-detected; absent when there is nothing to show) */}
                {!reduced && assist && (
                  <AssistenzBlock
                    summary={assist.summary}
                    draft={assist.draft}
                    konfidenz={resolveAssistKonfidenz(assist, mail.konfidenz)}
                    onCopyDraft={copyDraft}
                    onUseDraft={() => setComposeOpen(true)}
                  />
                )}
              </TabsContent>

              <TabsContent value="felder">
                <dl className="divide-y divide-border overflow-hidden rounded-md border">
                  {mail.felder.map((f) => (
                    <div
                      key={f.label}
                      className="flex items-baseline justify-between gap-4 px-3 py-2.5"
                    >
                      <dt className="text-sm text-muted-foreground">{f.label}</dt>
                      <dd className="text-pretty text-sm font-medium text-foreground">{f.value}</dd>
                    </div>
                  ))}
                </dl>
              </TabsContent>

              <TabsContent value="pipeline">
                <ol className="flex flex-col">
                  {mail.pipeline.map((step, i) => {
                    const isLast = i === mail.pipeline.length - 1;
                    return (
                      <li key={step.label} className="relative flex gap-3 pb-4 last:pb-0">
                        {!isLast && (
                          <span
                            aria-hidden
                            className="absolute bottom-0 left-[11px] top-6 w-px bg-border"
                          />
                        )}
                        <span className="relative z-10 flex size-6 shrink-0 items-center justify-center">
                          {step.status === 'done' ? (
                            <CircleCheck className="size-5 text-sem-ok" />
                          ) : step.status === 'current' ? (
                            <CircleDot className="size-5 text-sem-info" />
                          ) : (
                            <Circle className="size-5 text-muted-foreground/40" />
                          )}
                        </span>
                        <span
                          className={cn(
                            'pt-0.5 text-sm',
                            step.status === 'todo' ? 'text-muted-foreground' : 'text-foreground',
                          )}
                        >
                          {step.label}
                        </span>
                      </li>
                    );
                  })}
                </ol>
              </TabsContent>

              <TabsContent value="anhaenge">
                {/* U5: attachments=null + attachments_unavailable on the wire = the LIVE
                    listing failed - say so loudly, never "Keine Anhänge" (rule #1). */}
                {mail.anhaengeUnavailable ? (
                  <p
                    role="alert"
                    className="flex items-center justify-center gap-2 py-6 text-center text-sm text-destructive"
                  >
                    <AlertTriangle className="size-4 shrink-0" />
                    Anhänge konnten nicht geladen werden.
                  </p>
                ) : mail.anhaenge.length > 0 ? (
                  <div className="flex flex-col gap-2">
                    {anhangFehler && (
                      <p role="alert" className="text-xs text-destructive">
                        {anhangFehler}
                      </p>
                    )}
                    <ul className="divide-y divide-border overflow-hidden rounded-md border">
                      {mail.anhaenge.map((a) => (
                        <li key={a.id ?? a.name} className="flex items-center gap-3 px-3 py-2.5">
                          <Paperclip className="size-4 shrink-0 text-muted-foreground" />
                          <span className="min-w-0 flex-1 truncate text-sm text-foreground">
                            {a.name}
                            {a.onDemand && (
                              <span className="ml-2 text-xs text-muted-foreground">
                                nicht in Brokly gespeichert
                              </span>
                            )}
                          </span>
                          <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                            {a.size}
                          </span>
                          {a.onDemand && a.id && (
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={`${a.name} herunterladen`}
                              onClick={async () => {
                                setAnhangFehler(null);
                                try {
                                  const blob = await apiClient.communications.downloadAttachment(
                                    mail.id,
                                    a.id!,
                                  );
                                  saveBlob(blob, a.name);
                                } catch {
                                  setAnhangFehler('Download fehlgeschlagen.');
                                }
                              }}
                            >
                              <Download className="size-4" />
                            </Button>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <p className="py-6 text-center text-sm text-muted-foreground">Keine Anhänge.</p>
                )}
              </TabsContent>

              <TabsContent value="verlauf">
                <ol className="flex flex-col">
                  {mail.verlauf.map((item, i) => {
                    const isLast = i === mail.verlauf.length - 1;
                    return (
                      <li key={i} className="relative flex gap-3 pb-4 last:pb-0">
                        {!isLast && (
                          <span
                            aria-hidden
                            className="absolute bottom-0 left-[13px] top-7 w-px bg-border"
                          />
                        )}
                        {item.actor === 'ki' ? (
                          <span className="relative z-10 flex size-[26px] shrink-0 items-center justify-center rounded-md bg-sem-info-bg text-sem-info ring-2 ring-card">
                            <Sparkles className="size-3.5" />
                          </span>
                        ) : (
                          <span className="relative z-10 flex size-[26px] shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-semibold text-muted-foreground ring-2 ring-card">
                            LM
                          </span>
                        )}
                        <div className="flex min-w-0 flex-1 items-baseline justify-between gap-3 pt-0.5">
                          <span className="text-sm leading-snug text-foreground/90">{item.text}</span>
                          <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                            {item.time}
                          </span>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              </TabsContent>
            </div>
          </Tabs>
        </div>

        {/* Tags + Notizen Panel (toggleable) */}
        {panelOpen && (
          <aside className="flex w-64 shrink-0 flex-col gap-4 overflow-y-auto border-l bg-muted/20 p-4">
            <section className="flex flex-col gap-2">
              <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Tags
              </h3>
              <div className="flex flex-wrap gap-1.5">
                {mail.tags.length > 0 ? (
                  mail.tags.map((tag) => (
                    <Badge key={tag} variant="neutral">
                      {tag}
                    </Badge>
                  ))
                ) : (
                  <span className="text-sm text-muted-foreground">Keine Tags.</span>
                )}
                <button
                  type="button"
                  className="inline-flex h-5 items-center rounded-md border border-dashed px-1.5 text-xs text-muted-foreground transition-colors hover:border-foreground/30 hover:text-foreground"
                >
                  + Tag
                </button>
              </div>
            </section>

            <div className="border-t border-border" />

            <section className="flex min-h-0 flex-1 flex-col gap-2">
              <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Interne Notizen
              </h3>
              <textarea
                defaultValue={mail.notiz ?? ''}
                placeholder="Notiz für das Team …"
                aria-label="Interne Notiz"
                className="min-h-32 flex-1 resize-none rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            </section>
          </aside>
        )}
      </div>

      {/* Antworten — honest compose modal (UI-U9): draft-only, no fake send */}
      <ComposeDialog
        mail={mail}
        open={composeOpen}
        initialDraft={
          drafts[mail.id] ?? {
            // Parse "Name <email>" → bare addr-spec so the type="email" input validates.
            to: parseAddrSpec(mail.absender),
            betreff: `Re: ${mail.betreff}`,
            text: assist?.draft ?? mail.kiAntwort,
          }
        }
        onOpenChange={setComposeOpen}
        onSaveDraft={(d) => {
          setDrafts((prev) => ({ ...prev, [mail.id]: d }));
          setComposeOpen(false);
        }}
        sendingMailbox={sendingMailbox}
        onConnectRequest={onConnectRequest}
        commId={commId}
        onSend={
          onReplySend
            ? async (d) => {
                await onReplySend(d);
                setComposeOpen(false);
              }
            : undefined
        }
      />

      {/* Aufgabe-Dialog (hand-rolled modal) */}
      {taskOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md overflow-hidden rounded-lg border bg-card shadow-lg">
            <div className="flex items-center justify-between border-b p-4">
              <h2 className="font-semibold text-foreground">Folgeaufgabe erstellen</h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setTaskOpen(false)}
                aria-label="Schliessen"
                className="size-7"
              >
                <X className="size-4" />
              </Button>
            </div>
            <form
              className="flex flex-col gap-4 p-4"
              onSubmit={(e) => {
                e.preventDefault();
                onCreateTask(mail);
                setTaskOpen(false);
              }}
            >
              <div className="flex flex-col gap-1.5">
                <label htmlFor="task-titel" className="text-xs text-muted-foreground">Titel</label>
                <input
                  id="task-titel"
                  type="text"
                  value={taskTitel}
                  onChange={(e) => setTaskTitel(e.target.value)}
                  className="h-9 rounded-md border border-border bg-background px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="task-beschreibung" className="text-xs text-muted-foreground">Beschreibung</label>
                <textarea
                  id="task-beschreibung"
                  value={taskBeschreibung}
                  onChange={(e) => setTaskBeschreibung(e.target.value)}
                  className="min-h-24 resize-none rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </div>
              <div className="flex items-center justify-end gap-2 border-t pt-4">
                <Button type="button" variant="outline" onClick={() => setTaskOpen(false)}>
                  Abbrechen
                </Button>
                <Button type="submit">
                  <ClipboardList className="size-4" />
                  Folgeaufgabe erstellen
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
